// View engine: filter → sort → group tasks for List / Board / Table / Calendar / Gantt.

import type { FilterRule, GroupBy, Location, Status, Task, ViewSettings } from '@shared/types';
import { PRIORITIES } from '@shared/constants';
import type { Tables } from './store';
import { byOrder, statusOf, statusesForLocation, tasksIn } from './hierarchy';
import { startOfDay, startOfWeek, endOfWeek, addWeeks, isToday, isTomorrow, isBefore } from 'date-fns';

export interface TaskGroup {
  key: string;
  label: string;
  color?: string;
  /** status for status groups — used for pill rendering and new-task defaults */
  status?: Status;
  icon?: string;
  /** default values applied to a task created inside this group */
  defaults: Partial<Task>;
  tasks: Task[];
}

export interface QueryResult {
  groups: TaskGroup[];
  /** every task that passed filters (including subtasks) */
  matched: Task[];
  /** children per parent among matched tasks, ordered */
  children: Map<string, Task[]>;
  total: number;
}

export interface QueryOptions {
  meId: string;
  search?: string;
  /** include subtasks as top-level rows regardless of view setting */
  flatten?: boolean;
}

// ---------------------------------------------------------------------------
// field access

export function fieldSortValue(db: Tables, task: Task, field: string): string | number | null {
  switch (field) {
    case 'name':
      return task.name.toLowerCase();
    case 'assignee':
      return task.assigneeIds.length ? (db.users[task.assigneeIds[0]]?.name ?? '').toLowerCase() : null;
    case 'dueDate':
      return task.dueDate;
    case 'startDate':
      return task.startDate;
    case 'priority':
      return task.priority;
    case 'status': {
      const s = statusOf(db, task);
      const rank = { open: 0, unstarted: 1, custom: 2, done: 3, closed: 4 }[s.type];
      return rank * 1000 + s.orderIndex;
    }
    case 'dateCreated':
      return task.createdAt;
    case 'dateUpdated':
      return task.updatedAt ?? task.createdAt;
    case 'dateClosed':
      return task.closedAt;
    case 'timeEstimate':
      return task.timeEstimate;
    case 'taskType':
      return task.taskTypeId ? (db.taskTypes[task.taskTypeId]?.name ?? '').toLowerCase() : null;
    case 'createdBy':
      return (db.users[task.createdBy]?.name ?? '').toLowerCase();
    case 'list':
      return (db.lists[task.listId]?.name ?? '').toLowerCase();
    case 'taskId':
      return task.id;
    default:
      if (field.startsWith('cf:')) {
        const cf = db.customFields[field.slice(3)];
        const v = task.customFields[field.slice(3)];
        if (v === undefined || v === null || v === '') return null;
        if (cf?.type === 'dropdown') return cf.options?.find((o) => o.id === v)?.orderIndex ?? null;
        if (typeof v === 'boolean') return v ? 1 : 0;
        if (Array.isArray(v)) return v.length;
        return typeof v === 'number' ? v : String(v).toLowerCase();
      }
      return null;
  }
}

function compareValues(a: string | number | null, b: string | number | null) {
  if (a === b) return 0;
  if (a === null) return 1; // empty last
  if (b === null) return -1;
  return a < b ? -1 : 1;
}

// ---------------------------------------------------------------------------
// filters

function dateMatches(ts: number | null, op: FilterRule['operator'], value: unknown): boolean {
  const now = new Date();
  switch (op) {
    case 'set':
      return ts !== null;
    case 'not_set':
      return ts === null;
    case 'today':
      return ts !== null && isToday(ts);
    case 'overdue':
      return ts !== null && ts < startOfDay(now).getTime();
    case 'this_week':
      return ts !== null && ts >= startOfWeek(now).getTime() && ts <= endOfWeek(now).getTime();
    case 'next_week': {
      const s = startOfWeek(addWeeks(now, 1));
      return ts !== null && ts >= s.getTime() && ts <= endOfWeek(s).getTime();
    }
    case 'gt':
      return ts !== null && ts > Number(value);
    case 'lt':
      return ts !== null && ts < Number(value);
    case 'is':
      return ts !== null && startOfDay(ts).getTime() === startOfDay(Number(value)).getTime();
    default:
      return true;
  }
}

function setMatches(actual: string[], op: FilterRule['operator'], value: unknown): boolean {
  const wanted = Array.isArray(value) ? (value as string[]) : value === undefined || value === null ? [] : [String(value)];
  switch (op) {
    case 'is':
    case 'any':
      return wanted.length === 0 || wanted.some((w) => actual.includes(w));
    case 'all':
      return wanted.every((w) => actual.includes(w));
    case 'is_not':
    case 'none':
      return !wanted.some((w) => actual.includes(w));
    case 'set':
      return actual.length > 0;
    case 'not_set':
      return actual.length === 0;
    default:
      return true;
  }
}

export function ruleMatches(db: Tables, task: Task, rule: FilterRule, meId: string): boolean {
  const value = Array.isArray(rule.value) ? (rule.value as string[]).map((v) => (v === 'me' ? meId : v)) : rule.value === 'me' ? meId : rule.value;
  switch (rule.field) {
    case 'status': {
      const s = statusOf(db, task);
      // status values are stored as lowercase names so they work across lists
      return setMatches([s.name.toLowerCase()], rule.operator, value);
    }
    case 'assignee':
      return setMatches(task.assigneeIds, rule.operator, value);
    case 'priority':
      return setMatches(task.priority ? [String(task.priority)] : [], rule.operator, Array.isArray(value) ? value.map(String) : value);
    case 'tags':
      return setMatches(task.tagIds, rule.operator, value);
    case 'taskType':
      return setMatches(task.taskTypeId ? [task.taskTypeId] : [], rule.operator, value);
    case 'list':
      return setMatches([task.listId], rule.operator, value);
    case 'createdBy':
      return setMatches([task.createdBy], rule.operator, value);
    case 'dueDate':
      return dateMatches(task.dueDate, rule.operator, value);
    case 'startDate':
      return dateMatches(task.startDate, rule.operator, value);
    case 'dateCreated':
      return dateMatches(task.createdAt, rule.operator, value);
    case 'name':
      return rule.operator === 'contains' ? task.name.toLowerCase().includes(String(value ?? '').toLowerCase()) : true;
    default:
      if (rule.field.startsWith('cf:')) {
        const fid = rule.field.slice(3);
        const v = task.customFields[fid];
        const cf = db.customFields[fid];
        if (!cf) return true;
        if (cf.type === 'dropdown' || cf.type === 'labels' || cf.type === 'people') {
          const actual = v === undefined || v === null ? [] : Array.isArray(v) ? (v as string[]) : [String(v)];
          return setMatches(actual, rule.operator, value);
        }
        if (cf.type === 'date') return dateMatches(typeof v === 'number' ? v : null, rule.operator, value);
        if (cf.type === 'checkbox') return rule.operator === 'set' ? v === true : rule.operator === 'not_set' ? v !== true : true;
        const empty = v === undefined || v === null || v === '';
        if (rule.operator === 'set') return !empty;
        if (rule.operator === 'not_set') return empty;
        if (rule.operator === 'contains') return String(v ?? '').toLowerCase().includes(String(value ?? '').toLowerCase());
        if (rule.operator === 'gt') return Number(v) > Number(value);
        if (rule.operator === 'lt') return Number(v) < Number(value);
        if (rule.operator === 'is') return String(v ?? '') === String(value ?? '');
        return true;
      }
      return true;
  }
}

// ---------------------------------------------------------------------------
// grouping

const DUE_BUCKETS = ['overdue', 'today', 'tomorrow', 'this_week', 'next_week', 'later', 'none'] as const;
const DUE_LABEL: Record<(typeof DUE_BUCKETS)[number], string> = {
  overdue: 'Overdue',
  today: 'Today',
  tomorrow: 'Tomorrow',
  this_week: 'This week',
  next_week: 'Next week',
  later: 'Future',
  none: 'No due date',
};

export function dueBucket(ts: number | null): (typeof DUE_BUCKETS)[number] {
  if (ts === null) return 'none';
  const now = new Date();
  if (isBefore(ts, startOfDay(now))) return 'overdue';
  if (isToday(ts)) return 'today';
  if (isTomorrow(ts)) return 'tomorrow';
  if (ts <= endOfWeek(now).getTime()) return 'this_week';
  if (ts <= endOfWeek(addWeeks(now, 1)).getTime()) return 'next_week';
  return 'later';
}

function groupTasks(db: Tables, loc: Location, tasks: Task[], settings: ViewSettings, includeEmptyStatuses: boolean): TaskGroup[] {
  const groupBy: GroupBy = settings.groupBy;
  const groups = new Map<string, TaskGroup>();
  const ensure = (key: string, init: () => Omit<TaskGroup, 'tasks' | 'key'>) => {
    let g = groups.get(key);
    if (!g) {
      g = { key, tasks: [], ...init() };
      groups.set(key, g);
    }
    return g;
  };

  if (groupBy === 'none') {
    return [{ key: 'all', label: 'Tasks', defaults: {}, tasks }];
  }

  if (groupBy === 'status') {
    const statuses = statusesForLocation(db, loc);
    const visible = settings.showClosed ? statuses : statuses.filter((s) => s.type !== 'closed');
    for (const s of includeEmptyStatuses ? visible : []) {
      ensure(s.name.toLowerCase(), () => ({ label: s.name, color: s.color, status: s, defaults: loc.type === 'list' ? { statusId: s.id } : {} }));
    }
    for (const t of tasks) {
      const s = statusOf(db, t);
      const g = ensure(s.name.toLowerCase(), () => ({ label: s.name, color: s.color, status: s, defaults: loc.type === 'list' ? { statusId: s.id } : {} }));
      g.tasks.push(t);
    }
    const order = new Map(statuses.map((s, i) => [s.name.toLowerCase(), i]));
    const out = [...groups.values()].sort((a, b) => (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99));
    return settings.groupDir === 'desc' ? out.reverse() : out;
  }

  if (groupBy === 'assignee') {
    for (const t of tasks) {
      if (t.assigneeIds.length === 0) ensure('none', () => ({ label: 'Unassigned', defaults: { assigneeIds: [] } })).tasks.push(t);
      for (const u of t.assigneeIds) {
        ensure(u, () => ({ label: db.users[u]?.name ?? 'Unknown', color: db.users[u]?.color, defaults: { assigneeIds: [u] } })).tasks.push(t);
      }
    }
    const out = [...groups.values()].sort((a, b) => (a.key === 'none' ? 1 : b.key === 'none' ? -1 : a.label.localeCompare(b.label)));
    return settings.groupDir === 'desc' ? out.reverse() : out;
  }

  if (groupBy === 'priority') {
    for (const p of PRIORITIES) groups.set(String(p.value), { key: String(p.value), label: p.label, color: p.color, icon: 'Flag', defaults: { priority: p.value }, tasks: [] });
    groups.set('none', { key: 'none', label: 'No priority', icon: 'Flag', defaults: { priority: null }, tasks: [] });
    for (const t of tasks) groups.get(t.priority ? String(t.priority) : 'none')!.tasks.push(t);
    const out = [...groups.values()].filter((g) => g.tasks.length > 0);
    return settings.groupDir === 'desc' ? out.reverse() : out;
  }

  if (groupBy === 'dueDate') {
    for (const b of DUE_BUCKETS) groups.set(b, { key: b, label: DUE_LABEL[b], defaults: {}, tasks: [] });
    for (const t of tasks) groups.get(dueBucket(t.dueDate))!.tasks.push(t);
    const out = [...groups.values()].filter((g) => g.tasks.length > 0);
    return settings.groupDir === 'desc' ? out.reverse() : out;
  }

  if (groupBy === 'taskType') {
    const types = Object.values(db.taskTypes).sort(byOrder);
    const fallback = types.find((t) => t.builtin === 'task');
    for (const t of tasks) {
      const tt = (t.taskTypeId && db.taskTypes[t.taskTypeId]) || fallback;
      const key = tt?.id ?? 'task';
      ensure(key, () => ({ label: tt?.name ?? 'Task', icon: tt?.icon ?? 'CircleDot', color: tt?.color ?? undefined, defaults: { taskTypeId: tt?.builtin === 'task' ? null : (tt?.id ?? null) } })).tasks.push(t);
    }
    const rank = new Map(types.map((t, i) => [t.id, i]));
    const out = [...groups.values()].sort((a, b) => (rank.get(a.key) ?? 99) - (rank.get(b.key) ?? 99));
    return settings.groupDir === 'desc' ? out.reverse() : out;
  }

  if (groupBy === 'tags') {
    for (const t of tasks) {
      if (t.tagIds.length === 0) ensure('none', () => ({ label: 'No tags', defaults: {} })).tasks.push(t);
      for (const tagId of t.tagIds) {
        const tag = db.tags[tagId];
        ensure(tagId, () => ({ label: tag?.name ?? 'tag', color: tag?.color, defaults: { tagIds: [tagId] } })).tasks.push(t);
      }
    }
    return [...groups.values()].sort((a, b) => (a.key === 'none' ? 1 : b.key === 'none' ? -1 : a.label.localeCompare(b.label)));
  }

  if (groupBy === 'list') {
    for (const t of tasks) {
      const l = db.lists[t.listId];
      ensure(t.listId, () => ({ label: l?.name ?? 'List', icon: 'List', defaults: { listId: t.listId } })).tasks.push(t);
    }
    return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  if (groupBy.startsWith('cf:')) {
    const fid = groupBy.slice(3);
    const cf = db.customFields[fid];
    if (cf && (cf.type === 'dropdown' || cf.type === 'labels')) {
      for (const o of [...(cf.options ?? [])].sort(byOrder)) groups.set(o.id, { key: o.id, label: o.name, color: o.color, defaults: { customFields: { [fid]: cf.type === 'labels' ? [o.id] : o.id } }, tasks: [] });
      groups.set('none', { key: 'none', label: `No ${cf.name}`, defaults: {}, tasks: [] });
      for (const t of tasks) {
        const v = t.customFields[fid];
        const ids = v === undefined || v === null ? [] : Array.isArray(v) ? (v as string[]) : [String(v)];
        if (ids.length === 0) groups.get('none')!.tasks.push(t);
        for (const id of ids) groups.get(id)?.tasks.push(t);
      }
      return [...groups.values()].filter((g) => g.tasks.length > 0);
    }
    if (cf?.type === 'checkbox') {
      groups.set('yes', { key: 'yes', label: `${cf.name}: Yes`, defaults: { customFields: { [fid]: true } }, tasks: [] });
      groups.set('no', { key: 'no', label: `${cf.name}: No`, defaults: {}, tasks: [] });
      for (const t of tasks) groups.get(t.customFields[fid] === true ? 'yes' : 'no')!.tasks.push(t);
      return [...groups.values()].filter((g) => g.tasks.length > 0);
    }
  }

  return [{ key: 'all', label: 'Tasks', defaults: {}, tasks }];
}

// ---------------------------------------------------------------------------

export function sortTasks(db: Tables, tasks: Task[], sort: ViewSettings['sort']): Task[] {
  if (!sort.length) return [...tasks].sort((a, b) => a.orderIndex - b.orderIndex || a.createdAt - b.createdAt);
  const keyed = tasks.map((t) => ({ t, keys: sort.map((s) => fieldSortValue(db, t, s.field)) }));
  keyed.sort((a, b) => {
    for (let i = 0; i < sort.length; i++) {
      const c = compareValues(a.keys[i], b.keys[i]);
      if (c !== 0) return sort[i].dir === 'desc' ? (a.keys[i] === null || b.keys[i] === null ? c : -c) : c;
    }
    return a.t.orderIndex - b.t.orderIndex;
  });
  return keyed.map((k) => k.t);
}

export function runQuery(db: Tables, loc: Location, settings: ViewSettings, opts: QueryOptions & { includeEmptyStatuses?: boolean }): QueryResult {
  const all = tasksIn(db, loc);
  const search = opts.search?.trim().toLowerCase();
  const passes = (t: Task) => {
    if (!settings.showClosed && statusOf(db, t).type === 'closed') return false;
    if (settings.meMode && !t.assigneeIds.includes(opts.meId)) return false;
    if (search && !t.name.toLowerCase().includes(search) && !t.id.includes(search)) return false;
    const rules = settings.filters.rules;
    if (rules.length) {
      const results = rules.map((r) => ruleMatches(db, t, r, opts.meId));
      if (settings.filters.op === 'and' ? results.includes(false) : !results.includes(true)) return false;
    }
    return true;
  };

  const matched = all.filter(passes);
  const matchedIds = new Set(matched.map((t) => t.id));
  const flat = opts.flatten || settings.subtasks === 'separate' || !!search || settings.meMode || settings.filters.rules.length > 0;

  const children = new Map<string, Task[]>();
  const roots: Task[] = [];
  for (const t of matched) {
    if (!flat && t.parentId && matchedIds.has(t.parentId)) {
      const arr = children.get(t.parentId) ?? [];
      arr.push(t);
      children.set(t.parentId, arr);
    } else if (flat || !t.parentId || !matchedIds.has(t.parentId)) {
      // in nested mode, orphaned subtasks (parent filtered out) surface at root
      roots.push(t);
    }
  }
  for (const [k, arr] of children) children.set(k, sortTasks(db, arr, settings.sort));

  // In nested modes, subtasks whose parent is visible are not roots.
  const rootRows = flat ? roots : roots.filter((t) => !t.parentId || !matchedIds.has(t.parentId));
  const groups = groupTasks(db, loc, rootRows, settings, opts.includeEmptyStatuses ?? false);
  for (const g of groups) g.tasks = sortTasks(db, g.tasks, settings.sort);
  return { groups, matched, children, total: rootRows.length };
}

const childCountCache = new WeakMap<object, Map<string, number>>();

/** Direct subtask count for a task — shown as the "⤷ 4" hint. Cached per tasks-table snapshot. */
export function subtaskCount(db: Tables, taskId: string): number {
  let counts = childCountCache.get(db.tasks);
  if (!counts) {
    counts = new Map();
    for (const t of Object.values(db.tasks)) if (t.parentId && !t.archived) counts.set(t.parentId, (counts.get(t.parentId) ?? 0) + 1);
    childCountCache.set(db.tasks, counts);
  }
  return counts.get(taskId) ?? 0;
}
